import { jsPDF } from "jspdf";
import autoTableImport from "jspdf-autotable";

// jspdf-autotable interop varies — normalise to a callable.
const autoTable: (
  doc: jsPDF,
  options: Parameters<typeof autoTableImport>[1],
) => void =
  typeof autoTableImport === "function"
    ? (autoTableImport as never)
    : ((autoTableImport as unknown as { default: typeof autoTableImport })
        .default as never);

const BRAND_GREEN: [number, number, number] = [45, 134, 83];   // #2d8653
const BRAND_ORANGE: [number, number, number] = [231, 111, 0];  // #e76f00
const BRAND_DARK: [number, number, number] = [26, 26, 26];     // #1a1a1a
const MUTED: [number, number, number] = [110, 110, 110];
const RED: [number, number, number] = [192, 57, 43];

export interface OperationsHealthPdfData {
  businessName: string;
  city?: string;
  currencySymbol: string;
  generatedAt: Date;

  healthScore: number;
  healthLabel: string;
  healthColor: "green" | "orange" | "red";

  monthlyRevenue: number;
  laborPct: number;
  laborTarget: number;
  foodCostPct: number;
  foodTarget: number;

  pnl: {
    monthLabel: string;
    foodRevenue: number;
    beverageRevenue: number;
    foodCost: number;
    beverageCost: number;
    fohLabor: number;
    bohLabor: number;
    managementCost: number;
    rent: number;
    utilities: number;
    marketing: number;
    repairs: number;
    insurance: number;
    otherExpenses: number;
    totalRevenue: number;
    totalCogs: number;
    totalLabor: number;
    totalOpex: number;
    grossProfit: number;
    operatingProfit: number;
    netMargin: number;
  } | null;

  survivalScore: number | null;
  survivalLabel?: string | null;

  recommendations: { title: string; detail: string; tone: "warn" | "ok" }[];
  permits: { name: string; daysToExpiry: number }[];
}

function fmtMoney(symbol: string, n: number): string {
  return `${symbol}${Math.round(n).toLocaleString()}`;
}

function toneToRgb(t: "green" | "orange" | "red"): [number, number, number] {
  if (t === "green") return BRAND_GREEN;
  if (t === "orange") return BRAND_ORANGE;
  return RED;
}

/**
 * Generate a branded Operations Health PDF report. Uses jsPDF + autotable —
 * does NOT rely on window.print(). Always fits content cleanly across pages.
 */
export function exportOperationsHealthPdf(data: OperationsHealthPdfData): void {
  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 14;

  // ── Header band ─────────────────────────────────────────────
  doc.setFillColor(...BRAND_GREEN);
  doc.rect(0, 0, pageWidth, 28, "F");

  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(20);
  doc.text("Oplif", margin, 13);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.text("Operations Report", margin, 19);

  // Business name + date on the right
  doc.setFont("helvetica", "bold");
  doc.setFontSize(13);
  doc.text(data.businessName, pageWidth - margin, 13, { align: "right" });
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9.5);
  const dateStr = data.generatedAt.toLocaleDateString(undefined, {
    year: "numeric", month: "long", day: "numeric",
  });
  const subtitle = data.city ? `${data.city} · ${dateStr}` : dateStr;
  doc.text(subtitle, pageWidth - margin, 19, { align: "right" });

  let y = 40;

  // ── Operations Health Score (hero) ──────────────────────────
  const heroH = 32;
  const heroColor = toneToRgb(data.healthColor);
  doc.setFillColor(...heroColor);
  doc.roundedRect(margin, y, pageWidth - margin * 2, heroH, 3, 3, "F");

  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.text("OPERATIONS HEALTH SCORE", margin + 6, y + 8);

  doc.setFont("helvetica", "bold");
  doc.setFontSize(34);
  doc.text(`${data.healthScore}`, margin + 6, y + 24);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(11);
  doc.text("/ 100", margin + 6 + doc.getTextWidth(`${data.healthScore}`) + 2, y + 24);

  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.text(data.healthLabel, pageWidth - margin - 6, y + 24, { align: "right" });

  y += heroH + 8;

  // ── Key metrics row ─────────────────────────────────────────
  doc.setTextColor(...BRAND_DARK);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.text("Key Metrics", margin, y);
  y += 5;

  const metricBoxW = (pageWidth - margin * 2 - 6) / 3;
  const metricBoxH = 22;
  const metrics: Array<{ label: string; value: string; tone: [number, number, number] }> = [
    {
      label: "Monthly Revenue",
      value: fmtMoney(data.currencySymbol, data.monthlyRevenue),
      tone: BRAND_GREEN,
    },
    {
      label: `Labor % (target ≤ ${data.laborTarget}%)`,
      value: `${data.laborPct.toFixed(1)}%`,
      tone: data.laborPct > data.laborTarget ? BRAND_ORANGE : BRAND_GREEN,
    },
    {
      label: `Food Cost % (target ≤ ${data.foodTarget}%)`,
      value: `${data.foodCostPct.toFixed(1)}%`,
      tone: data.foodCostPct > data.foodTarget ? BRAND_ORANGE : BRAND_GREEN,
    },
  ];
  metrics.forEach((m, i) => {
    const x = margin + i * (metricBoxW + 3);
    doc.setDrawColor(220, 220, 220);
    doc.setLineWidth(0.3);
    doc.roundedRect(x, y, metricBoxW, metricBoxH, 2, 2, "S");
    // Accent bar
    doc.setFillColor(...m.tone);
    doc.rect(x, y, 2.5, metricBoxH, "F");
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(...MUTED);
    doc.text(m.label, x + 6, y + 7);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(15);
    doc.setTextColor(...BRAND_DARK);
    doc.text(m.value, x + 6, y + 17);
  });
  y += metricBoxH + 10;

  // ── P&L Statement ───────────────────────────────────────────
  if (data.pnl) {
    if (y > pageHeight - 110) { doc.addPage(); y = margin; }
    doc.setTextColor(...BRAND_DARK);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.text("Profit & Loss Statement", margin, y);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(...MUTED);
    doc.text(data.pnl.monthLabel, pageWidth - margin, y, { align: "right" });
    y += 4;

    const cs = data.currencySymbol;
    const m = data.pnl;
    const fm = (n: number) => fmtMoney(cs, n);
    const pctStr = (n: number) => `${n.toFixed(1)}%`;

    const rows: Array<[string, string, string]> = [
      ["REVENUE", "", ""],
      ["  Food revenue", fm(m.foodRevenue), ""],
      ["  Beverage revenue", fm(m.beverageRevenue), ""],
      ["  Total revenue", fm(m.totalRevenue), "100.0%"],
      ["COST OF GOODS SOLD", "", ""],
      ["  Food cost", fm(m.foodCost), m.foodRevenue > 0 ? pctStr((m.foodCost / m.foodRevenue) * 100) : "—"],
      ["  Beverage cost", fm(m.beverageCost), m.beverageRevenue > 0 ? pctStr((m.beverageCost / m.beverageRevenue) * 100) : "—"],
      ["  Total COGS", fm(m.totalCogs), m.totalRevenue > 0 ? pctStr((m.totalCogs / m.totalRevenue) * 100) : "—"],
      ["LABOR", "", ""],
      ["  FOH labor", fm(m.fohLabor), ""],
      ["  BOH labor", fm(m.bohLabor), ""],
      ["  Management", fm(m.managementCost), ""],
      ["  Total labor", fm(m.totalLabor), m.totalRevenue > 0 ? pctStr((m.totalLabor / m.totalRevenue) * 100) : "—"],
      ["OPERATING EXPENSES", "", ""],
      ["  Rent", fm(m.rent), ""],
      ["  Utilities", fm(m.utilities), ""],
      ["  Marketing", fm(m.marketing), ""],
      ["  Repairs & maintenance", fm(m.repairs), ""],
      ["  Insurance", fm(m.insurance), ""],
      ["  Other expenses", fm(m.otherExpenses), ""],
      ["  Total operating expenses", fm(m.totalOpex), m.totalRevenue > 0 ? pctStr((m.totalOpex / m.totalRevenue) * 100) : "—"],
      ["BOTTOM LINE", "", ""],
      ["  Gross profit", fm(m.grossProfit), ""],
      ["  Operating profit", fm(m.operatingProfit), ""],
      ["  Net profit margin", "", pctStr(m.netMargin)],
    ];

    autoTable(doc, {
      startY: y,
      head: [["Line item", "Amount", "% of revenue"]],
      body: rows,
      margin: { left: margin, right: margin },
      styles: {
        font: "helvetica",
        fontSize: 9,
        cellPadding: 3.5,
        textColor: [40, 40, 40],
        lineColor: [225, 225, 225],
        lineWidth: 0.2,
      },
      headStyles: {
        fillColor: BRAND_DARK,
        textColor: [255, 255, 255],
        fontStyle: "bold",
      },
      columnStyles: {
        0: { cellWidth: "auto" },
        1: { halign: "right", cellWidth: 38 },
        2: { halign: "right", cellWidth: 32 },
      },
      didParseCell: (hookData) => {
        const label = String(hookData.row.raw?.[0] ?? "").trim();
        const isSection =
          label === "REVENUE" || label === "COST OF GOODS SOLD" ||
          label === "LABOR" || label === "OPERATING EXPENSES" ||
          label === "BOTTOM LINE";
        if (isSection) {
          hookData.cell.styles.fontStyle = "bold";
          hookData.cell.styles.fillColor = [245, 245, 245];
          hookData.cell.styles.textColor = BRAND_GREEN as never;
        }
        // Highlight totals lines
        const isTotal =
          label.startsWith("  Total") || label === "  Net profit margin" ||
          label === "  Gross profit" || label === "  Operating profit";
        if (isTotal) {
          hookData.cell.styles.fontStyle = "bold";
          hookData.cell.styles.textColor = BRAND_DARK as never;
        }
      },
      theme: "grid",
    });

    const lastY = (doc as unknown as { lastAutoTable?: { finalY: number } })
      .lastAutoTable?.finalY;
    y = (lastY ?? y) + 10;
  }

  // ── Survival Score ──────────────────────────────────────────
  if (data.survivalScore !== null) {
    if (y > pageHeight - 40) { doc.addPage(); y = margin; }
    doc.setDrawColor(220, 220, 220);
    doc.setFillColor(250, 250, 247);
    doc.roundedRect(margin, y, pageWidth - margin * 2, 22, 2, 2, "FD");
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(...MUTED);
    doc.text("SURVIVAL SCORE", margin + 6, y + 8);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(18);
    doc.setTextColor(...BRAND_DARK);
    doc.text(`${data.survivalScore}`, margin + 6, y + 18);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.text("/ 100", margin + 6 + doc.getTextWidth(`${data.survivalScore}`) + 2, y + 18);
    if (data.survivalLabel) {
      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      doc.setTextColor(...MUTED);
      doc.text(data.survivalLabel, pageWidth - margin - 6, y + 14, { align: "right" });
    }
    y += 30;
  }

  // ── AI Recommendations ──────────────────────────────────────
  if (data.recommendations.length > 0) {
    if (y > pageHeight - 40) { doc.addPage(); y = margin; }
    doc.setTextColor(...BRAND_DARK);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.text("AI Recommendations", margin, y);
    y += 6;

    data.recommendations.forEach((r) => {
      const titleLines = doc.splitTextToSize(r.title, pageWidth - margin * 2 - 8);
      const detailLines = doc.splitTextToSize(r.detail, pageWidth - margin * 2 - 8);
      const blockH = 6 + titleLines.length * 5 + detailLines.length * 4.5 + 4;
      if (y + blockH > pageHeight - 18) { doc.addPage(); y = margin; }

      const accent = r.tone === "warn" ? BRAND_ORANGE : BRAND_GREEN;
      doc.setFillColor(...accent);
      doc.rect(margin, y, 1.5, blockH - 2, "F");

      doc.setFont("helvetica", "bold");
      doc.setFontSize(10);
      doc.setTextColor(...BRAND_DARK);
      doc.text(titleLines, margin + 5, y + 5);

      doc.setFont("helvetica", "normal");
      doc.setFontSize(9);
      doc.setTextColor(...MUTED);
      doc.text(detailLines, margin + 5, y + 5 + titleLines.length * 5 + 1);

      y += blockH;
    });
    y += 4;
  }

  // ── Permits section (optional) ──────────────────────────────
  if (data.permits.length > 0) {
    if (y > pageHeight - 40) { doc.addPage(); y = margin; }
    doc.setTextColor(...BRAND_DARK);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.text("Compliance — Upcoming Renewals", margin, y);
    y += 4;
    autoTable(doc, {
      startY: y,
      head: [["Permit", "Status"]],
      body: data.permits.slice(0, 8).map((p) => [
        p.name,
        p.daysToExpiry < 0
          ? `Expired ${Math.abs(p.daysToExpiry)}d ago`
          : `Renew in ${p.daysToExpiry}d`,
      ]),
      margin: { left: margin, right: margin },
      styles: { font: "helvetica", fontSize: 9, cellPadding: 3.5, lineColor: [225, 225, 225], lineWidth: 0.2 },
      headStyles: { fillColor: BRAND_DARK, textColor: [255, 255, 255], fontStyle: "bold" },
      columnStyles: { 1: { halign: "right", cellWidth: 50 } },
      theme: "grid",
    });
  }

  // ── Footer on every page ────────────────────────────────────
  const pageCount = doc.getNumberOfPages();
  for (let p = 1; p <= pageCount; p++) {
    doc.setPage(p);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(150, 150, 150);
    doc.text("oplif.com", margin, pageHeight - 8);
    doc.text(`Page ${p} of ${pageCount}`, pageWidth - margin, pageHeight - 8, { align: "right" });
  }

  const safeName = data.businessName.replace(/[^a-z0-9]+/gi, "-").toLowerCase() || "business";
  const dateSlug = data.generatedAt.toISOString().slice(0, 10);
  doc.save(`oplif-operations-report-${safeName}-${dateSlug}.pdf`);
}